import { ApolloClient } from "@apollo/client"
import { getAccessToken, getTokens } from "@auth/customerAccount"
import createContentfulClient from "@cms/createApolloClient"
import { GetCheckoutCartItemProductData } from "@cms/graphql/queries"
import {
  CHECKOUT_KEY,
  DISCOUNT_KEY,
  LAST_PASSED_DISCOUNT_APPLIED_KEY,
  PASSED_DISCOUNT_KEY,
  UTM_PARAMS_KEY,
} from "@constants/storageKeys"
import { formatInventoryData } from "@hooks/useMultiWarehouseInventory"
import { RootState } from "@redux/store"
import { createModel } from "@rematch/core"
import createShopifyCartClient from "@shopify/createApolloCartClient"
import {
  AddCartLine,
  ApplyDiscount,
  CreateCart,
  CreateCartWithBuyer,
  RemoveCartLine,
  RemoveDiscount,
  UpdateCartBuyerIdentity,
  UpdateCartLine,
} from "@shopify/graphql/mutations"
import { GetCart } from "@shopify/graphql/queries"
import { CartData, CartLineItem, UserError } from "@shopify/types"
import { getInventoryByLocation } from "@utils/api"
import { CountryCode, resolveCartCountryCode } from "@utils/cartMarket"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { getIn } from "@utils/index"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import * as R from "ramda"

import {
  AddToCartPayload,
  CartItem,
  CartProducts,
  LocalCheckout,
  ToastProduct,
} from "../types"
import {
  buildLocalCheckout,
  createCheckoutLineItem,
  formatContentfulCartProductsData,
  getMissingCartProductIds,
  graphQLRequest,
} from "../utils"
import { RootModel } from "./index"

const CHECKOUT_NOTE = "Checkout created via UAG v2"
export const AUTOMATIC_DISCOUNT_APPLICATION = "AutomaticDiscountApplication"
export const DISCOUNT_CODE_APPLICATION = "DiscountCodeApplication"
export const MANUAL_CODE_APPLICATION = "ManualDiscountApplication"
export const SCRIPT_DISCOUNT_APPLICATION = "ScriptDiscountApplication"

const getDataLayer = (): any[] | undefined => {
  if (typeof window === "undefined") {
    return undefined
  }

  const w = window as any
  w.dataLayer = w.dataLayer || []
  return w.dataLayer
}

const getDesiredCartCountry = (state: RootState): CountryCode => {
  const geoCountryCode = state.customer?.customerLocation
    ?.customerCountryCode as CountryCode | undefined
  const { country } = resolveCartCountryCode({
    geoCountryCode,
  })
  return country
}

const ensureCartCountry = async (
  client,
  cartId: string,
  countryCode: CountryCode
) => {
  await client.mutate({
    mutation: UpdateCartBuyerIdentity,
    variables: {
      buyerIdentity: { countryCode },
      cartCountry: countryCode,
      cartId,
    },
  })
}

export interface CheckoutState {
  cartProducts: CartProducts
  checkoutDataFetched: boolean
  checkoutId: null | string
  checkoutVerified: boolean
  data: CartData
  loadingCheckoutData: boolean
  localCheckout: LocalCheckout
  showErrorMessage: boolean
  showPromoToast: boolean
  toastProduct: null | ToastProduct
}

const checkout = createModel<RootModel>()({
  effects: (dispatch: any) => ({
    // -> Product Details
    addToCart({ cartProduct, showPromoToast }: AddToCartPayload, rootState) {
      dispatch.checkout.updateCartProducts({
        ...rootState.checkout.cartProducts,
        [cartProduct.sku]: cartProduct,
      })

      if (rootState.checkout.checkoutId) {
        const lines = getIn(rootState, "checkout.data.lines.edges", [])
        const linesWithProduct = lines.filter(
          (item: { node: CartLineItem }) => {
            return item.node.merchandise?.id === cartProduct.shopifyVariantId
          }
        )
        const line =
          linesWithProduct?.length > 1
            ? linesWithProduct.find((item: { node: CartLineItem }) => {
                return item.node.discountAllocations.length === 0
              })
            : linesWithProduct?.length
              ? linesWithProduct[0]
              : null
        const lineIndex = line
          ? lines.findIndex((item: { node: CartLineItem }) => {
              return item.node.id === line.node.id
            })
          : -1
        const quantity = (line && getIn(line, "node.quantity")) || 0

        dispatch.checkout.updateCheckoutItem({
          cartItem: {
            ...cartProduct,
            quantity: quantity + 1,
          },
          checkoutId: rootState.checkout.checkoutId,
          lineIndex,
        })
      } else {
        dispatch.checkout.createCheckout({ ...cartProduct, quantity: 1 })
      }

      if (showPromoToast) {
        dispatch.checkout.updateShowPromoToast(true)
        dispatch.checkout.clearShowPromoToast()
      } else {
        dispatch.checkout.updateToastProduct(
          R.pick(
            [
              "customPrint",
              "productSlug",
              "productName",
              "variantName",
              "variantDevice",
              "device",
              "image",
            ],
            cartProduct
          ) as ToastProduct
        )

        dispatch.checkout.clearToastProduct()
      }
    },

    clearCheckout() {
      writeLocalStorage(CHECKOUT_KEY, null)
      writeLocalStorage(DISCOUNT_KEY, null)
    },

    // -> Cart HOC
    async clearShowErrorMessage() {
      await new Promise((resolve) => {
        setTimeout(resolve, 5000)
      })

      dispatch.checkout.setShowErrorMessage(false)
    },

    async clearShowPromoToast() {
      await new Promise((resolve) => {
        setTimeout(resolve, 5000)
      })

      dispatch.checkout.updateShowPromoToast(false)
    },

    async clearToastProduct() {
      await new Promise((resolve) => {
        setTimeout(resolve, 5000)
      })

      dispatch.checkout.updateToastProduct(null)
    },

    async createCheckout(cartItem: CartItem, rootState) {
      const checkoutItems = [createCheckoutLineItem(cartItem)]
      dispatch.checkout.updateCartProducts({
        [cartItem.sku]: cartItem,
      })
      dispatch.checkout.setLoadingCheckoutData(true)

      const passedDiscount = getLocalStorage(PASSED_DISCOUNT_KEY)
      const lastPassedDiscount = getLocalStorage(
        LAST_PASSED_DISCOUNT_APPLIED_KEY
      )

      const cartCountry = getDesiredCartCountry(rootState)
      const lines = checkoutItems
      const note = CHECKOUT_NOTE

      const tokens = getTokens()
      const customerAccessToken = tokens?.access_token || null

      const client: ApolloClient<any> = createShopifyCartClient()
      const mutation = customerAccessToken ? CreateCartWithBuyer : CreateCart
      const variables: any = { cartCountry, lines, note }

      if (customerAccessToken) {
        variables.customerAccessToken = customerAccessToken
      }

      const { data, errors } = await graphQLRequest(client.mutate, {
        mutation,
        variables,
      })

      dispatch.checkout.setLoadingCheckoutData(false)

      const userErrors: Array<UserError> = getIn(
        data,
        "cartCreate.userErrors",
        []
      )

      if (errors || (userErrors && userErrors.length)) {
        writeLocalStorage(UTM_PARAMS_KEY, {})
        dispatch.checkout.setShowErrorMessage(true)
        return
      }

      const checkout: CartData = getIn(data, "cartCreate.cart")
      dispatch.checkout.setCheckoutId(checkout.id)
      dispatch.checkout.updateCheckoutData(checkout)
      dispatch.checkout.setCheckoutDataFetched(true) // Prevent refetch race condition
      dispatch.customer.updateCartBuyerIdentity({ cartId: checkout.id })

      const dataLayer = getDataLayer()
      if (dataLayer) {
        const items = getIn(checkout, "lines.edges", [])
        const formattedItems =
          items
            .filter(({ node }) => !!node)
            .map(({ node }) => ({
              id: node.merchandise?.id?.replace(
                "gid://shopify/ProductVariant/",
                ""
              ),
              pricePerItem: node.cost?.amountPerQuantity?.amount,
              quantity: node.quantity,
            })) || []

        dataLayer.push({
          customerEmail: getIn(
            rootState,
            "customer.data.emailAddress.emailAddress"
          ),
          event: "maestra_set_cart",
          items: formattedItems,
        })
      }

      if (passedDiscount && passedDiscount !== lastPassedDiscount) {
        await dispatch.checkout.updateDiscount(passedDiscount, rootState)
        writeLocalStorage(LAST_PASSED_DISCOUNT_APPLIED_KEY, passedDiscount)
      }
    },

    async fetchCheckout(checkoutId, rootState) {
      dispatch.checkout.setLoadingCheckoutData(true)

      const client: ApolloClient<any> = createShopifyCartClient()
      const { data, errors } = await graphQLRequest(client.query, {
        fetchPolicy: "no-cache",
        query: GetCart,
        variables: {
          cartCountry: getDesiredCartCountry(rootState),
          cartId: checkoutId,
        },
      })

      dispatch.checkout.setLoadingCheckoutData(false)

      if (errors) {
        dispatch.checkout.clearCheckout()
        dispatch.checkout.setCheckoutDataFetched(true)
        return
      }

      const checkout: CartData = getIn(data, "cart", undefined)
      const lines = getIn(checkout, "lines.edges", []) as Array<{
        node: CartLineItem
      }>

      if (checkout && lines.length > 0) {
        const skus = lines.map(({ node: { merchandise } }) => {
          return merchandise.sku
        }) as Array<string>

        if (!rootState.checkout.checkoutVerified && skus.length > 0) {
          const inventoryData = await getInventoryByLocation(skus)
          const formattedInventoryData = inventoryData
            ? formatInventoryData(inventoryData)
            : {}
          const customerLocation = rootState.customer.customerLocationFetched
            ? rootState.customer.customerLocation
            : await dispatch.customer.fetchCustomerLocation()

          const availableLineItems = lines.filter((line) => {
            const availabilityData = getAvailabilityByRegion(
              customerLocation.customerCountryCode,
              line.node.merchandise.sku,
              formattedInventoryData
            )

            const availableQuantity = availabilityData?.quantity || 0
            return availableQuantity
          })

          dispatch.checkout.setCheckoutVerified(true)

          if (lines.length !== availableLineItems.length) {
            dispatch.checkout.clearCheckout()
            dispatch.checkout.setCheckoutDataFetched(true)
            return
          }
        }

        dispatch.checkout.setCheckoutId(checkout.id)
        dispatch.checkout.updateCheckoutData(checkout)

        const passedDiscount = getLocalStorage(PASSED_DISCOUNT_KEY)
        const lastPassedDiscount = getLocalStorage(
          LAST_PASSED_DISCOUNT_APPLIED_KEY
        )

        if (passedDiscount && passedDiscount !== lastPassedDiscount) {
          await dispatch.checkout.updateDiscount(passedDiscount, rootState)
          writeLocalStorage(LAST_PASSED_DISCOUNT_APPLIED_KEY, passedDiscount)
        }
      } else {
        dispatch.checkout.clearCheckout()
      }
      dispatch.checkout.setCheckoutDataFetched(true)
    },

    async fetchContentfulCartProducts(ids) {
      const client: ApolloClient<any> = createContentfulClient()

      let productVariantData = {}

      if (ids.length > 0) {
        const { data, errors } = await graphQLRequest(client.query, {
          query: GetCheckoutCartItemProductData,
          variables: { ids },
        })

        if (!errors) {
          productVariantData = data
        }
      }

      const formattedData = formatContentfulCartProductsData(productVariantData)
      return formattedData
    },

    setCheckoutId(checkoutId) {
      writeLocalStorage(CHECKOUT_KEY, checkoutId)
    },

    // -> Cart HOC
    updateCart(
      { cartItem, lineIndex }: { cartItem: CartItem; lineIndex: number },
      rootState
    ) {
      dispatch.checkout.updateCheckoutItem({
        cartItem,
        checkoutId: rootState.checkout.checkoutId,
        lineIndex,
      })
    },

    async updateCheckoutData(cartData: CartData, rootState) {
      const missingCartProductIds = getMissingCartProductIds(
        cartData,
        rootState.checkout.cartProducts
      )
      let updatedCartProducts = rootState.checkout.cartProducts

      if (missingCartProductIds.length) {
        const formattedData =
          await dispatch.checkout.fetchContentfulCartProducts(
            missingCartProductIds
          )

        updatedCartProducts = {
          ...rootState.checkout.cartProducts,
          ...formattedData,
        }

        dispatch.checkout.updateCartProducts(updatedCartProducts)
      }

      dispatch.checkout.updateCheckout({
        data: cartData,
        id: cartData.id,
        localCheckout: buildLocalCheckout(cartData, updatedCartProducts),
      })
    },

    async updateCheckoutItem(
      {
        cartItem,
        checkoutId,
        lineIndex,
      }: {
        cartItem: CartItem
        checkoutId: string
        lineIndex: number
      },
      rootState
    ) {
      const lines = getIn(rootState, "checkout.data.lines.edges", [])
      let lineItem
      let lineItemId

      if (
        lineIndex > -1 &&
        lines[lineIndex] &&
        lines[lineIndex].node?.merchandise?.id === cartItem.shopifyVariantId
      ) {
        lineItem = lines[lineIndex]
        lineItemId = getIn(lineItem, "node.id", null)
      }

      const client: ApolloClient<any> = createShopifyCartClient()
      const cartCountry = getDesiredCartCountry(rootState)

      await ensureCartCountry(client, checkoutId, cartCountry)

      const variables: any = {
        cartCountry,
        cartId: checkoutId,
      }
      let mutation: any

      if (!lineItem) {
        mutation = AddCartLine
        variables.lines = [createCheckoutLineItem(cartItem)]
      } else if (lineItemId && cartItem.quantity === 0) {
        mutation = RemoveCartLine
        variables.lineIds = [lineItemId]
      } else if (lineItemId) {
        mutation = UpdateCartLine
        variables.lines = [
          {
            id: lineItemId,
            quantity: cartItem.quantity,
          },
        ]
      }

      dispatch.checkout.setLoadingCheckoutData(true)

      const { data, errors } = await graphQLRequest(client.mutate, {
        mutation,
        variables,
      })

      dispatch.checkout.setLoadingCheckoutData(false)

      const mutationKey =
        mutation === AddCartLine
          ? "cartLinesAdd"
          : mutation === RemoveCartLine
            ? "cartLinesRemove"
            : "cartLinesUpdate"

      const checkoutErrors: Array<UserError> = getIn(
        data,
        `${mutationKey}.userErrors`,
        []
      )

      if (errors || (checkoutErrors && checkoutErrors.length)) {
        console.log(checkoutErrors?.length ? checkoutErrors : errors)
        dispatch.checkout.setShowErrorMessage(true)
        return
      }

      const checkout: CartData = getIn(data, `${mutationKey}.cart`)
      dispatch.checkout.updateCheckoutData(checkout)

      const dataLayer = getDataLayer()
      if (dataLayer) {
        const items = getIn(checkout, "lines.edges", [])
        const formattedItems =
          items
            .filter(({ node }) => !!node)
            .map(({ node }) => ({
              id: node.merchandise?.id?.replace(
                "gid://shopify/ProductVariant/",
                ""
              ),
              pricePerItem: node.cost?.amountPerQuantity?.amount,
              quantity: node.quantity,
            })) || []

        dataLayer.push({
          customerEmail: getIn(
            rootState,
            "customer.data.emailAddress.emailAddress"
          ),
          event: "maestra_set_cart",
          items: formattedItems,
        })
      }
    },

    async updateDiscount(discountCode: string | undefined, rootState) {
      const client: ApolloClient<any> = createShopifyCartClient()
      const checkoutId = rootState.checkout.checkoutId

      if (discountCode) {
        dispatch.checkout.setLoadingCheckoutData(true)

        const { data, errors } = await graphQLRequest(client.mutate, {
          mutation: ApplyDiscount,
          variables: {
            cartId: checkoutId,
            discountCode,
          },
        })

        dispatch.checkout.setLoadingCheckoutData(false)

        const checkoutErrors: Array<UserError> = getIn(
          data,
          "cartDiscountCodesUpdate.userErrors",
          []
        )

        if (errors || (checkoutErrors && checkoutErrors.length)) {
          return getIn(checkoutErrors, "0", checkoutErrors)
        }

        const checkout: CartData = getIn(data, "cartDiscountCodesUpdate.cart")

        dispatch.checkout.updateCheckoutData(checkout)

        return {}
      } else {
        dispatch.checkout.setLoadingCheckoutData(true)

        const { data, errors } = await graphQLRequest(client.mutate, {
          mutation: RemoveDiscount,
          variables: {
            cartId: checkoutId,
          },
        })

        dispatch.checkout.setLoadingCheckoutData(false)

        const checkoutErrors: Array<UserError> = getIn(
          data,
          "cartDiscountCodesUpdate.userErrors",
          []
        )

        if (errors || (checkoutErrors && checkoutErrors.length)) {
          console.log(checkoutErrors?.length ? checkoutErrors : errors)
          dispatch.checkout.setShowErrorMessage(true)
          return
        }

        const checkout: CartData = getIn(data, "cartDiscountCodesUpdate.cart")
        dispatch.checkout.updateCheckoutData(checkout)
      }
    },
  }),
  name: "checkout",
  reducers: {
    clearCheckout(state: CheckoutState) {
      return {
        ...state,
        checkoutId: null,
        data: {} as CartData,
        localCheckout: {} as LocalCheckout,
      }
    },

    setCheckoutDataFetched(state: CheckoutState, checkoutDataFetched: boolean) {
      return {
        ...state,
        checkoutDataFetched,
      }
    },

    setCheckoutVerified(state: CheckoutState, checkoutVerified: boolean) {
      return {
        ...state,
        checkoutVerified,
      }
    },

    setLoadingCheckoutData(state: CheckoutState, loadingCheckoutData: boolean) {
      return {
        ...state,
        loadingCheckoutData,
      }
    },

    // -> Cart HOC
    setShowErrorMessage(state: CheckoutState, showErrorMessage: boolean) {
      return {
        ...state,
        showErrorMessage,
      }
    },

    updateCartProducts(state: CheckoutState, data: CartProducts) {
      return {
        ...state,
        cartProducts: {
          ...state.cartProducts,
          ...data,
        },
      }
    },

    updateCheckout(
      state: CheckoutState,
      cartData: { data: CartData; id: string; localCheckout: LocalCheckout }
    ) {
      return {
        ...state,
        checkoutId: cartData.id,
        data: cartData.data,
        localCheckout: cartData.localCheckout,
      }
    },

    updateShowPromoToast(state: CheckoutState, showPromoToast: boolean) {
      return {
        ...state,
        showPromoToast,
      }
    },

    updateToastProduct(
      state: CheckoutState,
      toastProduct: null | ToastProduct
    ) {
      return {
        ...state,
        toastProduct: toastProduct,
      }
    },
  },
  // https://github.com/rematch/rematch/issues/816
  selectors: (slice) => ({
    automaticDiscountAllocations() {
      return slice((checkout) => {
        const lines = getIn(checkout, "data.lines.edges", []) as Array<{
          node: CartLineItem
        }>

        return lines.reduce((discountAllocations, { node }) => {
          if (node.discountAllocations?.length) {
            const discount = node.discountAllocations[0]
            const sku = node.merchandise.sku
            discountAllocations[sku] = discount.discountedAmount.amount
          }

          return discountAllocations
        }, {})
      })
    },

    automaticDiscountApplied() {
      return slice((checkout) => {
        const discountCodes = getIn(
          checkout,
          "data.discountCodes",
          []
        ) as Array<any>
        return discountCodes.find(({ __typename }) => {
          return __typename === "CartAutomaticDiscountAllocation"
        })
      })
    },

    cartTotal() {
      return slice((checkout) => {
        return checkout.data?.cost?.totalAmount?.amount || "0.00"
      })
    },

    checkoutLink() {
      return slice((checkout) => {
        let checkoutUrl = getIn(checkout, "data.checkoutUrl")
        const customerAccessToken = getAccessToken()

        if (checkoutUrl && customerAccessToken) {
          try {
            const u = new URL(checkoutUrl)
            u.searchParams.set("logged_in", "true")
            checkoutUrl = u.toString()
          } catch {
            // no-op
          }
        }

        return checkoutUrl
      })
    },

    couponCodeApplied() {
      return slice((checkout) => {
        const discountCodes = getIn(
          checkout,
          "data.discountCodes",
          []
        ) as Array<any>
        let discountCode
        discountCodes.forEach(({ __typename, applicable, code }) => {
          if (__typename === "CartDiscountCode" && applicable) {
            discountCode = code
          }
        })
        return discountCode
      })
    },

    quantity() {
      return slice((checkout) => {
        if (!checkout?.localCheckout?.items) {
          return 0
        }

        return checkout.localCheckout.items.reduce((total, item) => {
          return total + item.quantity
        }, 0)
      })
    },
  }),
  state: {
    cartProducts: {} as CartProducts,
    checkoutDataFetched: false,
    checkoutId: getLocalStorage(CHECKOUT_KEY),
    checkoutVerified: false,
    data: {} as CartData,
    loadingCheckoutData: false,
    localCheckout: {} as LocalCheckout,
    showErrorMessage: false, // Something went wrong. Your cart has been adjusted.
    showPromoToast: false,
    toastProduct: null,
  } as CheckoutState,
})

export default checkout
